import Phaser from 'phaser';
import {
    MENTOR_PLATFORM_X,
    TILE_DICTIONARY_MAP,
    TILE_SIZE,
    TileType,
    calculatePortalSurfaceY,
    RoomBox,
} from '@blockworld/shared';
import { SpeechBubble } from '../ui/SpeechBubble';
import { TokiPonaAudio, VOICE_PROFILES } from '../toki/TokiPonaAudio';
import { getGlyphIndex, getWord } from '../toki/TokiPonaData';

// ============================================================
// MENTOR NPC BEHAVIOR
// "jan pu" (Sage) — greets players entering the spawn platform room.
// Speaks "toki" in the lili (Child) voice, comments on held items,
// and recites a 7-word poem about the item.
// ============================================================

const MENTOR_VOICE_KEY = 'ante'; // changed voice
const GREETING_WORDS = [108];    // "toki"
const COOLDOWN_MS = 5000;        // 5 seconds between greetings
const LINE_DELAY_MS = 1000;      // 1 second gap between speech lines

// ============================================================
// POEM LOADING — reads from centralized poems.txt document.
// Format: "word1 word2\tword1 word2 word3 word4 word5 word6 word7"
// Source of truth: packages/toki_pona_game/poems.txt
// ============================================================

/** Cached poems: word-pair key → poem (as glyph index array). */
let poemCache: Map<string, number[]> | null = null;
let poemLoadPromise: Promise<void> | null = null;

/** Load poems from the centralized poems.txt document. Fire-and-forget safe. */
export async function loadPoems(): Promise<void> {
    if (poemCache) return;
    if (poemLoadPromise) { await poemLoadPromise; return; }

    poemLoadPromise = (async () => {
        try {
            const response = await fetch('poems.txt');
            const text = await response.text();
            poemCache = new Map();

            for (const line of text.split('\n')) {
                const tabIndex = line.indexOf('\t');
                if (tabIndex === -1) continue;
                const key = line.substring(0, tabIndex).trim();
                const poemStr = line.substring(tabIndex + 1).trim();
                if (!key || !poemStr) continue;

                poemCache.set(key, poemStr.split(/\s+/).map(w => getGlyphIndex(w)));
            }
        } catch (e) {
            console.error('[MentorBehavior] Failed to load poems:', e);
        }
    })();

    await poemLoadPromise;
}

/** Look up the 7-word poem for a tile type from the loaded poems.txt data. */
function getPoemForTile(tileType: TileType): number[] | null {
    if (!poemCache) return null;
    const pair = TILE_DICTIONARY_MAP[tileType];
    if (!pair) return null;

    const wordA = getWord(pair[0]);
    const wordB = getWord(pair[1]);
    if (!wordA || !wordB) return null;

    const key = `${wordA} ${wordB}`;
    return poemCache.get(key) ?? null;
}

type MentorState =
    | 'idle'
    | 'greeting'
    | 'comment_pause'
    | 'commenting'
    | 'poem_pause'
    | 'poem'
    | 'cooldown';

export class MentorBehavior {
    private scene: Phaser.Scene;
    private audio: TokiPonaAudio;
    private worldSeed: number;

    private state: MentorState = 'idle';
    private wasInsideRoomBox = false;
    private activeBubble: SpeechBubble | null = null;
    private stateStartTime = 0;
    private pendingItemWords: number[] | null = null;
    private pendingPoemWords: number[] | null = null;

    /** Current facing direction (mutable — updated on player entry). */
    facing = 1; // 1 = right, -1 = left

    constructor(scene: Phaser.Scene, audio: TokiPonaAudio, seed: number) {
        this.scene = scene;
        this.audio = audio;
        this.worldSeed = seed;
        loadPoems(); // fire-and-forget — ready before player reaches mentor
    }

    /**
     * Call each frame from the game update loop.
     * Detects room box entry, manages speech state machine.
     */
    update(
        now: number,
        playerX: number,
        playerY: number,
        isHoldingBlock: boolean,
        heldBlockType: number,
        roomBoxes: RoomBox[],
        offsetX: number,
        offsetY: number,
    ): void {
        // Use the lower room box (spawn platform area)
        const lowerBox = roomBoxes[0];
        if (!lowerBox) return;

        const isInside =
            playerX >= lowerBox.x1 && playerX <= lowerBox.x2 &&
            playerY >= lowerBox.y1 && playerY <= lowerBox.y2;

        const justEntered = isInside && !this.wasInsideRoomBox;
        this.wasInsideRoomBox = isInside;

        // On entry: trigger greeting if idle
        if (justEntered && this.state === 'idle') {
            // Turn to face the entering player
            this.facing = playerX < MENTOR_PLATFORM_X ? -1 : 1;

            // Prepare item comment + poem if player is holding a block
            this.pendingItemWords = null;
            this.pendingPoemWords = null;
            if (isHoldingBlock && heldBlockType >= 0) {
                const dictEntry = TILE_DICTIONARY_MAP[heldBlockType as TileType];
                if (dictEntry) {
                    // "sina jo e [word1] [word2]"
                    this.pendingItemWords = [94, 19, 9, dictEntry[0], dictEntry[1]];
                    // Look up poem from centralized poems.txt
                    const poem = getPoemForTile(heldBlockType as TileType);
                    if (poem) {
                        this.pendingPoemWords = poem;
                    }
                }
            }

            // Start greeting
            this.state = 'greeting';
            this.stateStartTime = now;
            this.createBubble(GREETING_WORDS, offsetX, offsetY);
        }

        // State machine transitions
        switch (this.state) {
            case 'greeting':
                if (this.activeBubble && !this.activeBubble.isActive()) {
                    this.activeBubble = null;
                    if (this.pendingItemWords) {
                        this.state = 'comment_pause';
                        this.stateStartTime = now;
                    } else {
                        this.state = 'cooldown';
                        this.stateStartTime = now;
                    }
                }
                break;

            case 'comment_pause':
                if (now - this.stateStartTime >= LINE_DELAY_MS) {
                    this.facing = playerX < MENTOR_PLATFORM_X ? -1 : 1;
                    this.state = 'commenting';
                    this.stateStartTime = now;
                    this.createBubble(this.pendingItemWords!, offsetX, offsetY);
                    this.pendingItemWords = null;
                }
                break;

            case 'commenting':
                if (this.activeBubble && !this.activeBubble.isActive()) {
                    this.activeBubble = null;
                    if (this.pendingPoemWords) {
                        this.state = 'poem_pause';
                        this.stateStartTime = now;
                    } else {
                        this.state = 'cooldown';
                        this.stateStartTime = now;
                    }
                }
                break;

            case 'poem_pause':
                if (now - this.stateStartTime >= LINE_DELAY_MS) {
                    this.facing = playerX < MENTOR_PLATFORM_X ? -1 : 1;
                    this.state = 'poem';
                    this.stateStartTime = now;
                    this.createBubble(this.pendingPoemWords!, offsetX, offsetY);
                    this.pendingPoemWords = null;
                }
                break;

            case 'poem':
                if (this.activeBubble && !this.activeBubble.isActive()) {
                    this.activeBubble = null;
                    this.state = 'cooldown';
                    this.stateStartTime = now;
                }
                break;

            case 'cooldown':
                if (now - this.stateStartTime >= COOLDOWN_MS) {
                    this.state = 'idle';
                }
                break;
        }
    }

    /**
     * Called when the player presses attack within 1 tile of the mentor.
     * Turns the mentor to face the player and triggers a greeting if idle.
     */
    notifyPlayerAttention(playerX: number, offsetX: number, offsetY: number, now: number): void {
        this.facing = playerX < MENTOR_PLATFORM_X ? -1 : 1;

        // If idle, start a greeting so the mentor responds to the interaction
        if (this.state === 'idle') {
            this.state = 'greeting';
            this.stateStartTime = now;
            this.createBubble(GREETING_WORDS, offsetX, offsetY);
        }
    }

    /** Return the active speech bubble (if any) so constellation overlays can query it. */
    getActiveBubble(): SpeechBubble | null {
        return this.activeBubble && this.activeBubble.isActive() ? this.activeBubble : null;
    }

    /** Update the active speech bubble's screen position (call during render). */
    updateBubblePosition(offsetX: number, offsetY: number): void {
        if (this.activeBubble && this.activeBubble.isActive()) {
            const surfaceY = calculatePortalSurfaceY(this.worldSeed);
            const mentorY = surfaceY - 2;
            const screenX = MENTOR_PLATFORM_X * TILE_SIZE + offsetX;
            const screenY = mentorY * TILE_SIZE + offsetY;
            this.activeBubble.updatePosition(screenX, screenY);
        }
    }

    /** Clean up on scene/world reset. */
    destroy(): void {
        if (this.activeBubble) {
            this.activeBubble.destroy();
            this.activeBubble = null;
        }
        this.state = 'idle';
        this.wasInsideRoomBox = false;
        this.pendingItemWords = null;
        this.pendingPoemWords = null;
    }

    private createBubble(words: number[], offsetX: number, offsetY: number): void {
        if (this.activeBubble && this.activeBubble.isActive()) {
            this.activeBubble.destroy();
        }
        const surfaceY = calculatePortalSurfaceY(this.worldSeed);
        const mentorY = surfaceY - 2;
        const screenX = MENTOR_PLATFORM_X * TILE_SIZE + offsetX;
        const screenY = mentorY * TILE_SIZE + offsetY;

        this.activeBubble = new SpeechBubble({
            scene: this.scene,
            words,
            playerScreenX: screenX,
            playerScreenY: screenY,
            audio: this.audio,
            profile: VOICE_PROFILES[MENTOR_VOICE_KEY],
        });
    }
}
